/**
 * Loading an app's pages after the front door.
 *
 * The student and teacher apps keep their landing page and sign-in screen in
 * the first download and load every other page together, as one chunk, the
 * first time one is opened. See docs/FeatureDesigns/web-load-performance-current.md.
 */

import { use, type ComponentType } from 'react';

/**
 * Page components backed by one lazily imported module of pages.
 *
 * Unlike a `React.lazy` per page, a page whose module has already arrived
 * renders straight away: `use` reads the settled promise synchronously, so
 * moving between pages never shows a Suspense fallback once the chunk is in.
 * Only the first page shown waits, behind the nearest `<Suspense>`.
 */
export function pagesFrom<M extends Record<string, unknown>>(load: () => Promise<M>) {
  let loading: Promise<M> | undefined;
  const modules = () => {
    if (!loading) {
      loading = load();
      // A failed fetch is not cached, so a later render can try again.
      loading.catch(() => { loading = undefined; });
    }
    return loading;
  };

  function page<K extends keyof M & string>(name: K) {
    type P = M[K] extends ComponentType<infer Props> ? Props & object : never;
    function AppPage(props: P) {
      const Page = use(modules())[name] as ComponentType<object>;
      return <Page {...props} />;
    }
    AppPage.displayName = name;
    return AppPage;
  }

  /** Start fetching the pages now — from the sign-in screen, say, so they are
   *  in by the time the user is. Failures are left for the real load. */
  page.preload = () => { modules().catch(() => {}); };

  return page;
}

const RELOADED_AT_KEY = 'bb_stale_chunk_reload_at';

/**
 * Reload when a lazily loaded chunk is gone.
 *
 * A tab left open across a deploy still asks for the old build's chunk names,
 * and those no longer exist. Vite reports that as `vite:preloadError`; a
 * reload fetches the new build. At most one reload a minute, so a failure a
 * reload does not fix (offline, a broken deploy) reaches the error boundary
 * instead of looping.
 */
export function reloadOnStaleChunk(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', (event) => {
    const now = Date.now();
    try {
      const last = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
      if (now - last < 60_000) return;
      sessionStorage.setItem(RELOADED_AT_KEY, String(now));
    } catch {
      return; // no storage, no loop guard: let the error surface
    }
    event.preventDefault();
    window.location.reload();
  });
}
